import { useCallback, useEffect, useRef, useState } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { useInView } from 'react-intersection-observer';
import { checkAggregateACL, getUser } from '@frontend/auth-helpers';
import { useComponentEngagementShallowStore } from '@frontend/high-value-adoption';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';

export const useBillingRebrandVisibility = ({
  componentId,
  source,
  featureFlag,
}: {
  componentId: string;
  source?: string;
  featureFlag: string;
}) => {
  const { selectedLocationIds } = useAppScopeStore();
  const user = getUser();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const [isMarkedAsSeen, setIsMarkedAsSeen] = useState(false);

  // Unique source to track user interaction
  const trackingSource = `${user?.username || 'unknown'}-${source || 'unknown'}`;

  const { aggregateValue: hasBillingAccess } = checkAggregateACL(
    selectedLocationIds,
    [Permission.BILLING_INFORMATION_WRITE, Permission.BILLING_INFORMATION_READ],
    'any'
  );
  const isBillingRebrandBannerEnabled = getFeatureFlagValue(featureFlag);

  const isEligibleForPromo = hasBillingAccess && isBillingRebrandBannerEnabled;

  const { trackVisibility, getEngagementData, trackInteraction } = useComponentEngagementShallowStore(
    'trackVisibility',
    'getEngagementData',
    'trackInteraction'
  );

  const { visibilityCount, interactionCount } = getEngagementData({ componentId, source: trackingSource });

  // Persist banner visibility till component is mounted
  const persistBannerVisibilityRef = useRef(false);

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const markAsSeen = useCallback(() => {
    trackInteraction({
      source: trackingSource,
      componentId,
    });
    setIsMarkedAsSeen(true);
  }, [trackInteraction, trackingSource, componentId]);

  useEffect(() => {
    if (!isEligibleForPromo) {
      return;
    }
    if (inView) {
      trackVisibility({
        componentId,
        source: trackingSource,
      });
    }
  }, [inView, isEligibleForPromo, trackVisibility, trackingSource, componentId]);

  useEffect(() => {
    return () => {
      persistBannerVisibilityRef.current = false;
    };
  }, []);

  const showBanner =
    isEligibleForPromo &&
    (persistBannerVisibilityRef.current || visibilityCount < 1) &&
    !isMarkedAsSeen &&
    interactionCount < 1;

  if (showBanner && !persistBannerVisibilityRef.current) {
    persistBannerVisibilityRef.current = true;
  }

  return {
    showBanner,
    ref,
    markAsSeen,
  };
};
